import { Link } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { BarraProgreso, Badge, Button, Card, Desplegable, MetricCard, Rotulo, Stack, TituloPantalla } from "@/components";
import { formatFecha } from "@/lib/format";
import { ETIQUETA_ROL } from "@/lib/usuarios";
import { NovedadesTab } from "@/tabs/novedades/NovedadesTab";
import { type GrupoInicio, useInicioTab } from "./useInicioTab";

/** Pantalla de inicio. El fiduciante tiene la suya, simple: su cuenta y las novedades (Mati, 2026-10-19). */
export function InicioTab() {
  const { membresia } = useContextoApp();
  return membresia.rol === "fiduciante" ? <NovedadesTab /> : <InicioGeneral />;
}

/**
 * Admin, contador y escribano: una tarjeta por módulo y las últimas novedades de la obra.
 */
function InicioGeneral() {
  const t = useInicioTab();
  const { membresia } = useContextoApp();
  return (
    <Stack espacio={8} className="motion-safe:animate-aparecer">
      <TituloPantalla antetitulo={`${membresia.fideicomiso_nombre} · ${ETIQUETA_ROL[t.rol]}`} titulo="Hola, " destacado={t.nombre}>
        Lo principal del desarrollo, de un vistazo.
      </TituloPantalla>
      {t.cartelInstalar && (
        <Card>
          <div className="flex items-start justify-between gap-3">
            <p className="text-chico">
              Instalá la app para recibir avisos en el celular.{" "}
              <Link to="/perfil" className="font-medium text-primario underline">
                Cómo instalarla
              </Link>
            </p>
            <Button variante="fantasma" aria-label="Cerrar" onClick={t.cerrarCartel}>
              ×
            </Button>
          </div>
        </Card>
      )}
      {t.verTerreno && (
        <Link to="/terreno" className={CLASE_LINK}>
          Ver el terreno
          <span aria-hidden>→</span>
        </Link>
      )}
      {t.puestaEnMarchaError && <p className="text-chico text-texto-suave">{t.puestaEnMarchaError}</p>}
      {t.puestaEnMarcha &&
        (t.puestaEnMarcha.completa ? (
          <Desplegable
            abiertoInicial={t.puestaEnMarcha.abierto}
            onCambio={t.puestaEnMarcha.onCambio}
            titulo={
              <span className="flex items-center gap-2">
                {t.puestaEnMarcha.titulo}
                <Badge tono="exito">Completa</Badge>
              </span>
            }
          >
            {t.puestaEnMarcha.abierto && <GruposPuestaEnMarcha grupos={t.puestaEnMarcha.grupos} />}
          </Desplegable>
        ) : (
          <Stack espacio={3}>
            <Stack espacio={1}>
              <h2 className="text-[1.5rem] font-light tracking-tight">{t.puestaEnMarcha.titulo}</h2>
              <p className="text-chico text-texto-suave">El desarrollo se arma con el fideicomiso y el terreno; después viene la obra.</p>
            </Stack>
            <GruposPuestaEnMarcha grupos={t.puestaEnMarcha.grupos} />
          </Stack>
        ))}
      {t.sinDatos && <p className="text-texto-suave">Todavía no hay datos de la obra para mostrar.</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {t.tarjetaPrevision && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaPrevision.titulo} tono={t.tarjetaPrevision.tono} valor={t.tarjetaPrevision.valor} detalle={t.tarjetaPrevision.detalle} />
            {t.tarjetaPrevision.link && (
              <Link to={t.tarjetaPrevision.link} className={CLASE_LINK}>
                {t.tarjetaPrevision.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjetaAvance && (
          <Stack espacio={2}>
            <MetricCard
              titulo={t.tarjetaAvance.titulo}
              valor={t.tarjetaAvance.valor}
              debajoValor={t.tarjetaAvance.porcentaje != null && <BarraProgreso porcentaje={t.tarjetaAvance.porcentaje} tono="primario" etiqueta="Avance de obra" />}
              detalle={t.tarjetaAvance.detalle}
            />
            {t.tarjetaAvance.link && (
              <Link to={t.tarjetaAvance.link} className={CLASE_LINK}>
                {t.tarjetaAvance.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjeta && <MetricCard titulo={t.tarjeta.titulo} valor={t.tarjeta.valor} detalle={t.tarjeta.detalle} />}
        {t.tarjetaCuenta && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaCuenta.titulo} valor={t.tarjetaCuenta.valor} detalle={t.tarjetaCuenta.detalle} />
            {t.tarjetaCuenta.link && (
              <Link to={t.tarjetaCuenta.link} className={CLASE_LINK}>
                {t.tarjetaCuenta.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjetaUnidades && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaUnidades.titulo} valor={t.tarjetaUnidades.valor} detalle={t.tarjetaUnidades.detalle} />
            {t.tarjetaUnidades.link && (
              <Link to={t.tarjetaUnidades.link} className={CLASE_LINK}>
                {t.tarjetaUnidades.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjetaVentas && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaVentas.titulo} valor={t.tarjetaVentas.valor} detalle={t.tarjetaVentas.detalle} />
            {t.tarjetaVentas.link && (
              <Link to={t.tarjetaVentas.link} className={CLASE_LINK}>
                {t.tarjetaVentas.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjetaCaja && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaCaja.titulo} valor={t.tarjetaCaja.valor} detalle={t.tarjetaCaja.detalle} />
            {t.tarjetaCaja.link && (
              <Link to={t.tarjetaCaja.link} className={CLASE_LINK}>
                {t.tarjetaCaja.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
        {t.tarjetaPresupuesto && (
          <Stack espacio={2}>
            <MetricCard titulo={t.tarjetaPresupuesto.titulo} valor={t.tarjetaPresupuesto.valor} detalle={t.tarjetaPresupuesto.detalle} />
            {t.tarjetaPresupuesto.link && (
              <Link to={t.tarjetaPresupuesto.link} className={CLASE_LINK}>
                {t.tarjetaPresupuesto.textoLink}
                <span aria-hidden>→</span>
              </Link>
            )}
          </Stack>
        )}
      </div>
      {t.novedades && (
        <Card>
          <Stack espacio={4}>
            <Rotulo>Últimas novedades</Rotulo>
            <ul aria-label="Últimas novedades" className="flex flex-col divide-y divide-borde">
              {t.novedades.map((n) => (
                <li key={n.id} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:gap-6">
                  <span className="shrink-0 text-[0.6875rem] uppercase tracking-[0.16em] tabular-nums text-texto-suave sm:w-28">{formatFecha(n.fecha)}</span>
                  <span className="text-subtitulo font-light">{n.titulo}</span>
                </li>
              ))}
            </ul>
            <Link to="/avance" className={CLASE_LINK}>
              Ver todas las novedades
              <span aria-hidden>→</span>
            </Link>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

/** Link debajo de cada tarjeta ("Ver unidades"), terracota con flecha, como los de la presentación. */
const CLASE_LINK = "inline-flex items-center gap-1 px-1 text-chico font-medium text-primario hover:underline";

/** Las tres tarjetas de la puesta en marcha: fideicomiso y terreno (el desarrollo) y la obra, que viene después. */
function GruposPuestaEnMarcha({ grupos }: { grupos: GrupoInicio[] }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {grupos.map((g) => (
        <Card key={g.clave} role="region" aria-label={g.titulo} className={g.enEspera ? "border-dashed shadow-none" : undefined}>
          <Stack espacio={3}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[1.25rem] font-light tracking-tight">{g.titulo}</h3>
              {g.completo ? (
                <Badge tono="exito">Completo</Badge>
              ) : g.enEspera ? (
                <Badge tono="neutro">Después</Badge>
              ) : (
                <span className="text-chico text-texto-suave">
                  {g.completos} de {g.total}
                </span>
              )}
            </div>
            <p className="text-chico text-texto-suave">{g.descripcion}</p>
            <BarraProgreso porcentaje={(g.completos / g.total) * 100} tono={g.completo ? "exito" : "primario"} etiqueta={`Avance de ${g.titulo}`} />
            <ul aria-label={`Pasos de ${g.titulo}`} className="flex flex-col divide-y divide-borde">
              {g.pasos.map((p) => (
                <li key={p.clave} className="flex items-center gap-3 py-2">
                  <span aria-hidden className={p.completo ? "text-exito" : "text-texto-suave"}>
                    {p.completo ? "✓" : "○"}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-medium">{p.titulo}</span>
                    <span className="text-chico text-texto-suave">{p.completo ? "Listo" : p.detalle}</span>
                  </span>
                  <Link to={p.ruta} className={p.completo ? "shrink-0 text-chico text-primario" : "shrink-0 text-chico font-medium text-primario underline"}>
                    {p.completo ? "Ver" : "Cargar"}
                  </Link>
                </li>
              ))}
            </ul>
          </Stack>
        </Card>
      ))}
    </div>
  );
}
